import React, { useEffect, useMemo, useState, createContext, useContext } from 'react';
import LinearProgress from '@mui/material/LinearProgress';

import type { IOtherUser, IUser } from 'src/api/users-api';
import type { ILead, IProperty, ELeadStage, IStageHistory, ILeadPropertyInterest } from 'src/services/api';
import type { IShowing } from 'src/api/landlord-showings-api';
import type { IPropertyLabel } from 'src/services/api';
import { type LeadsGrouping, LeadsSortColumn } from 'src/pages/landlord/leads/types';
import { useShowings, usePropertyLabels, useProperties, useLeadStageHistory } from 'src/services/api';
import { useAuthenticatedState } from 'src/authenticated-state/context';
import { searchLeads } from 'src/pages/landlord/leads/utils/search-leads';
import { sortLeads } from 'src/pages/landlord/leads/utils/sort-leads';
import { filterLeads } from 'src/pages/landlord/leads/utils/filter-leads';
import {usePersistentState} from 'src/services/storage/use-persistent-state';

export enum LeadsDateFilter {
    Month = 'MONTH',
    Week = 'WEEK',
    Day = 'DAY',
}

export const leadDateFilterLabels: Record<LeadsDateFilter, string> = {
    [LeadsDateFilter.Month]: 'New this month',
    [LeadsDateFilter.Week]: 'New this week',
    [LeadsDateFilter.Day]: 'New today',
};

const leadDateFilters = Object.values(LeadsDateFilter) as LeadsDateFilter[];
export const leadsDateFilterOptions = leadDateFilters.map((filter) => ({
    value: filter,
    label: leadDateFilterLabels[filter]
}));

export type ILeadFilters = {
    assigneeIds: string[];

    ingestionSources: string[];
    ingestionMethods: string[];

    dateRange: LeadsDateFilter | undefined;

    leadStages: ELeadStage[];
    leadLabelIds: string[];

    propertyIds: string[];
    // When true, only conversations with no property will be shown.
    // We can overload the propertyId field to also represent the absence of a property.
    // But, this is more explicit and less error-prone.
    hasNoProperty: boolean;
    propertyLabelIds: string[];
};

// Helper types to make the code more concise.
// Must not be exported!
type Action<T> = React.Dispatch<React.SetStateAction<T>>;
type Context = ILeadsPageContext;

export interface ILeadsPageContext {
    loggedInUser: IUser;
    teamMembers: IOtherUser[];
    allLeads: ILead[];
    realLeads: ILead[];
    syntheticLead: ILead | undefined;
    leads: ILead[];
    properties: Pick<IProperty, 'id' | 'name'>[];
    propertyLabels: IPropertyLabel[];
    showings: IShowing[];
    stages?: IStageHistory[];
    interests?: ILeadPropertyInterest[];

    grouping?: LeadsGrouping;
    setGrouping: (grouping?: LeadsGrouping) => void;
    sortBy: LeadsSortColumn;
    setSortBy: Action<Context['sortBy']>;
    sortDirection: 'asc' | 'desc';
    setSortDirection: Action<Context['sortDirection']>;

    search: string;
    setSearch: Action<Context['search']>;
    isFilterOpen: boolean;
    setFilterOpen: Action<Context['isFilterOpen']>;
    filters: ILeadFilters;
    setFilters: Action<Context['filters']>;
    resetFilters: () => void;
    isFilterOrSearchActive: boolean;

    isCreateLeadModalOpen: boolean;
    setIsCreateLeadModalOpen: Action<Context['isCreateLeadModalOpen']>;
    isEditLeadLabelsModalOpen: boolean;
    setIsEditLeadLabelsModalOpen: Action<Context['isEditLeadLabelsModalOpen']>;

    selectedLeadUuids: string[];
    setSelectedLeadUuids: Action<Context['selectedLeadUuids']>;

    selectedLeadUuid: string;
    setSelectedLeadUuid: Action<Context['selectedLeadUuid']>;

    isLoading?: boolean
}

// @ts-expect-error lazy init
export const LeadsPageContext = createContext<ILeadsPageContext>();

export const useLeadsPageContext = () => {
    const context = useContext(LeadsPageContext);

    if (!context) {
        throw new Error('useLeadsPageContext must be used within a LeadsPageProvider');
    }

    return context;
};

export const INITIAL_FILTERS: ILeadFilters = {
    assigneeIds: [],
    ingestionSources: [],
    ingestionMethods: [],
    dateRange: undefined,
    leadStages: [],
    leadLabelIds: [],
    propertyIds: [],
    hasNoProperty: false,
    propertyLabelIds: [],
};

interface Props {
    children: React.ReactNode;
}

export default function LeadsPageProvider({ children }: Props) {
    const { loggedInUser, showArchived, leads, isLoading: isAuthStateDataLoading } = useAuthenticatedState();

    const [isCreateLeadModalOpen, setIsCreateLeadModalOpen] = useState(false);
    const [isEditLeadLabelsModalOpen, setIsEditLeadLabelsModalOpen] = useState(false);
    // used for stage timelines in lead sidebar
    const [selectedLeadUuid, setSelectedLeadUuid] = useState<string>('');

    const { data: properties } = useProperties();
    const { data: propertyLabels } = usePropertyLabels();
    const { data: showings } = useShowings();
    const { data: stageHistory } = useLeadStageHistory(selectedLeadUuid, { enabled: !!selectedLeadUuid });
    const stages = stageHistory?.stages;
    const interests = stageHistory?.interests;

    const teamMembers = useMemo(() => {
        if (!loggedInUser?.leasing_team_members) {
            return [];
        }

        return loggedInUser.leasing_team_members;
    }, [loggedInUser]);

    const realLeads = useMemo(() => {
        if (!leads) {
            return [];
        }

        return leads.filter((lead) => !lead.is_synthetic);
    }, [leads]);
    const syntheticLead = useMemo(() => {
        if (!leads) {
            return;
        }

        return leads.find((lead) => lead.is_synthetic);
    }, [leads]);

    const [search, setSearch] = useState('');

    const [filters, _setFilters] = usePersistentState(
        'leadFilters',
        { ...INITIAL_FILTERS }
    );

    const setFilters: React.Dispatch<React.SetStateAction<ILeadFilters>> = (value) => {
        if (typeof value === 'function') {
            return _setFilters(value(filters));
        }
        _setFilters(value as any);
    };

    const [isFilterOpen, setFilterOpen] = useState(false);

    const [sortBy, setSortBy] = useState<LeadsSortColumn>(LeadsSortColumn.INSERTED_AT);
    const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
    const [grouping, setGrouping] = useState<LeadsGrouping>();

    const [selectedLeadUuids, setSelectedLeadUuids] = useState<string[]>([]);

    const filteredLeads = useMemo(() => {
        let _filteredLeads = leads || [];
        _filteredLeads = _filteredLeads.filter((lead) => {
            if (showArchived) { return true; }
            return !lead.is_archived;
        });

        if (!filters) {
            return _filteredLeads;
        }

        _filteredLeads = filterLeads(_filteredLeads, properties, filters);
        _filteredLeads = searchLeads(_filteredLeads, properties, search);

        return _filteredLeads;
    }, [showArchived, leads, properties, search, filters]);

    const filteredAndSortedLeads = useMemo(() => {
        return sortLeads(filteredLeads, sortBy, sortDirection);
    }, [filteredLeads, sortBy, sortDirection]);

    // Reset selected leads when the tab, search or filter changes
    useEffect(() => {
        setSelectedLeadUuids([]);
    }, [search, filters]);

    const isLoading = !loggedInUser ||
        !leads ||
        !properties ||
        !propertyLabels ||
        !showings ||
        isAuthStateDataLoading;
    if (isLoading) {
        return (
            <LinearProgress sx={{ position: 'fixed', top: 0 }} />
        );
    }

    const resetFilters = () => {
        _setFilters(INITIAL_FILTERS);
    };

    const isFilterOrSearchActive = !!search ||
        !!filters?.dateRange ||
        !!filters?.assigneeIds.length ||
        !!filters?.ingestionSources.length ||
        !!filters?.ingestionMethods.length ||
        !!filters?.hasNoProperty ||
        !!filters?.propertyIds.length ||
        !!filters?.propertyLabelIds.length;

    return (
        <LeadsPageContext.Provider value={{
            loggedInUser,
            teamMembers,
            allLeads: leads || [],
            realLeads,
            syntheticLead,
            leads: filteredAndSortedLeads,
            properties,
            propertyLabels,
            showings,
            stages,
            interests,

            sortBy,
            setSortBy,
            sortDirection,
            setSortDirection,
            grouping,
            setGrouping,

            search,
            setSearch,
            filters,
            setFilters,
            resetFilters,
            isFilterOpen,
            setFilterOpen,
            isFilterOrSearchActive,

            isCreateLeadModalOpen,
            setIsCreateLeadModalOpen,
            isEditLeadLabelsModalOpen,
            setIsEditLeadLabelsModalOpen,

            selectedLeadUuids,
            setSelectedLeadUuids,

            selectedLeadUuid,
            setSelectedLeadUuid,

            isLoading: isAuthStateDataLoading
        }}
        >
            {children}
        </LeadsPageContext.Provider>
    );
}
